// Member multi-select for org channel create / membership edit.
import type { MemberRow } from "./workplaceMessagesTypes";

export type WorkplaceChannelMemberPickerProps = {
  members: MemberRow[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  /** Always kept selected (e.g. channel creator). */
  lockedIds?: string[];
  disabled?: boolean;
};

export function WorkplaceChannelMemberPicker({
  members,
  selectedIds,
  onChange,
  lockedIds = [],
  disabled = false,
}: WorkplaceChannelMemberPickerProps) {
  const locked = new Set(lockedIds);
  const selected = new Set(selectedIds);

  function toggle(id: string) {
    if (disabled || locked.has(id)) return;
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next]);
  }

  function selectAll() {
    if (disabled) return;
    onChange(members.map((m) => m.user_id));
  }

  function selectNone() {
    if (disabled) return;
    onChange([...locked]);
  }

  return (
    <div className="mt-2 space-y-1">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Members ({selectedIds.length})
        </p>
        <div className="flex gap-2 text-[10px]">
          <button
            type="button"
            disabled={disabled}
            onClick={selectAll}
            className="text-teal-800 hover:underline disabled:opacity-40 dark:text-teal-200"
          >
            All
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={selectNone}
            className="text-teal-800 hover:underline disabled:opacity-40 dark:text-teal-200"
          >
            Clear
          </button>
        </div>
      </div>
      <ul className="max-h-36 space-y-0.5 overflow-y-auto rounded-md border border-gray-200 p-1.5 dark:border-zinc-700">
        {members.length === 0 ? (
          <li className="px-1 py-1 text-[11px] text-gray-500">No teammates loaded.</li>
        ) : (
          members.map((m) => {
            const checked = selected.has(m.user_id);
            const isLocked = locked.has(m.user_id);
            return (
              <li key={m.user_id}>
                <label
                  className={[
                    "flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-[11px] hover:bg-gray-50 dark:hover:bg-zinc-800",
                    disabled ? "cursor-not-allowed opacity-60" : "",
                  ].join(" ")}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled || isLocked}
                    onChange={() => toggle(m.user_id)}
                    className="h-3.5 w-3.5 rounded border-gray-300"
                  />
                  <span className="min-w-0 truncate text-gray-800 dark:text-gray-100">
                    {m.name}
                    <span className="text-gray-400"> · L{m.trust_level}</span>
                    {isLocked ? <span className="text-gray-400"> · you</span> : null}
                  </span>
                </label>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
}
